import { GroupFieldsMeta } from 'module/types/filter-field/transformer/filter-transformer';
import { useNavigate, useParams } from '@rocketlane/rocket-router';
import { PeoplePageTemplate } from 'api/people-page-template/types';
import {
  useGetPeoplePageTemplateQuery,
  useGetTemporaryPeoplePageTemplate,
} from 'queries/people-page-template';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { ROUTE_PATHS } from 'routes/route-paths';
import { PeoplePageTemplateState } from './types';
import { apiToState, temporaryApiToState } from '../utils/api-to-state';

interface Props {
  fieldsLookup: GroupFieldsMeta;
  isFieldsLoading: boolean;
}
const newTemplateIdInUrl = 'new';

export const useManageTemplateState = ({
  fieldsLookup,
  isFieldsLoading,
}: Props): {
  isLoading: boolean;
  isEdit: boolean;
  templateState: PeoplePageTemplateState | undefined;
  setTemplateState: (next: Partial<PeoplePageTemplateState>) => void;
  originalTemplate: PeoplePageTemplate | undefined;
} => {
  const { peoplePageTemplateId: peoplePageTemplateIdString } = useParams<{
    peoplePageTemplateId: string;
  }>();
  const isEdit = peoplePageTemplateIdString !== newTemplateIdInUrl;
  const [templateState, setTemplateState] = useState<PeoplePageTemplateState>();
  const navigate = useNavigate();
  const temporaryPeoplePageTemplate = useGetTemporaryPeoplePageTemplate();
  const peoplePageTemplateId = peoplePageTemplateIdString
    ? parseInt(peoplePageTemplateIdString, 10)
    : -1;
  const { data: template, isLoading: isTemplateLoading } =
    useGetPeoplePageTemplateQuery(peoplePageTemplateId, {
      enabled: isEdit && peoplePageTemplateId !== -1,
    });
  const isContentLoading = isTemplateLoading;
  const navigateToListPage = useCallback(() => {
    navigate(
      `/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.PEOPLE_PAGE_TEMPLATE_LIST}`,
    );
  }, [navigate]);

  useEffect(() => {
    if (isContentLoading || isFieldsLoading) {
      return;
    }
    if (isEdit) {
      if (template == null) {
        navigateToListPage();

        return;
      } else {
        setTemplateState(apiToState(template, fieldsLookup));
      }
    } else if (temporaryPeoplePageTemplate == null) {
      navigateToListPage();

      return;
    } else {
      setTemplateState(
        temporaryApiToState(temporaryPeoplePageTemplate, fieldsLookup),
      );
    }
  }, [
    template,
    navigateToListPage,
    isContentLoading,
    isFieldsLoading,
    fieldsLookup,
    isEdit,
    temporaryPeoplePageTemplate,
  ]);
  const updateTemplateState = useCallback(
    (next: Partial<PeoplePageTemplateState>) => {
      setTemplateState((previous) => ({ ...previous!, ...next }));
    },
    [setTemplateState],
  );

  return {
    isLoading: isContentLoading,
    isEdit,
    templateState,
    setTemplateState: updateTemplateState,
    originalTemplate: template,
  };
};
